"use client"

import { DenunciaChatCard } from "@/components/denuncia/denuncia-chat-card"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type { Denuncia, Historico } from "@prisma/client"
import { AlertCircle } from "lucide-react"
import { useState } from "react"

type DenunciaWithHistorico = Denuncia & { historico: Historico[] }

export default function ConsultaV2Page() {
  const [codigo, setCodigo] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [denuncia, setDenuncia] = useState<DenunciaWithHistorico | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setDenuncia(null)

    try {
      const response = await fetch(`/api/denuncias/${codigo}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Erro ao buscar denúncia")
      }

      setDenuncia(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao buscar denúncia")
    } finally {
      setLoading(false)
    }
  }

  const handleEnviarMensagem = async (mensagem: string) => {
    setLoading(true)
    setError("")

    try {
      const response = await fetch(`/api/denuncias/${codigo}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mensagem }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Erro ao enviar mensagem")
      }

      setDenuncia((prev) => {
        if (!prev) return null
        return {
          ...prev,
          historico: [...prev.historico, data],
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao enviar mensagem")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Consultar Denúncia</CardTitle>
            <CardDescription>
              Digite o código de rastreamento para consultar o status da sua denúncia.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="codigo"
                  placeholder="Digite o código de rastreamento"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  required
                />
              </div>

              {error && !denuncia && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Erro</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={loading}>
                {loading ? "Buscando..." : "Consultar"}
              </Button>
            </form>

            {denuncia && (
              <div className="mt-8">
                <DenunciaChatCard
                  denuncia={denuncia}
                  error={error}
                  loading={loading}
                  onSendMessage={handleEnviarMensagem}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}